import React, { useEffect, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { AnswerItem, FormShowType, ImageSize, QuestionValidationTypes, ReduxState } from 'interface';
import { QuestionSLPRef, SLPQuestionType } from '../../questions.index';
import { OptionItemType } from 'utilities/slp/options/options.index';
import SLPOptionsPhoto, { SLPOptionPhotoRef } from 'utilities/slp/optionsPhoto/optionsPhoto.index';
import { extractTitle } from 'scripts/string';
import { createQuestionTitle } from '../questionsTypes.script';

const SLPPhotoOptionsQuestion: React.ForwardRefRenderFunction<
    QuestionSLPRef,
    ConnectedProps<typeof connector> & SLPQuestionType
> = (props: ConnectedProps<typeof connector> & SLPQuestionType, ref) => {
    React.useImperativeHandle(ref, () => ({
        validate: (e?: boolean) => validate(e),
        submit: (e?: boolean) => onSubmit(e),
    }));
    const lang = props.lang.SLP.questionsTypes;
    const question = props.question;
    const inputRef = useRef<SLPOptionPhotoRef>(null);
    const multi = !!question.validations?.find((i) => i.title === QuestionValidationTypes.selective)?.value;
    let size = ImageSize.medium;
    if (question.validations) {
        const valueSize = question.validations.find((i) => i.title === QuestionValidationTypes.pictureSize)?.value;
        if (valueSize) {
            if (valueSize.toString() === ImageSize.small.toString()) {
                size = ImageSize.small;
            } else if (valueSize.toString() === ImageSize.large.toString()) {
                size = ImageSize.large;
            }
        }
    }
    const items: OptionItemType[] = [];
    question.items
        ?.sort((a, b) => (a.order ? a.order : a.id) - (b.order ? b.order : b.id))
        .map((i) =>
            items.push({
                title: i.title,
                payload: i.id,
                image: i.image_file,
                value: i.value,
                archive: i.archive,
                isHidden: i.is_hidden,
            }),
        );
    const [title, setTitle] = useState(question.title);
    const index = props.answerList?.findIndex((i) => i.question.id === question.id);
    const [text, setText] = useState<string>();
    /* const optionValue = props.SLP.answerList?.find((i) => i.question.id === props.question.id)?.question_items;
    const value: OptionItemType[] = [];
    optionValue?.map((i) => value.push({ title: i.title, payload: i.payload })); */

    const [hint, setHint] = useState(question.description);

    useEffect(() => {
        if (question.description)
            setHint(
                createQuestionTitle(
                    question.description,
                    props.answerList,
                    props.answer?.answerer_info,
                    props.form.indicators,
                ),
            );
        setTitle(
            createQuestionTitle(question.title, props.answerList, props.answer?.answerer_info, props.form.indicators),
        );
    }, [props.answerList]);

    function validate(e?: boolean) {
        const value = inputRef.current?.getValue();
        if (question.is_required && value && value?.length < 1) {
            if (e) inputRef.current?.setError(multi ? lang.multiSelectEmp : lang.selectiveErr);
            return false;
        }
        return true;
    }

    function setAnswer() {
        const value = inputRef.current?.getValue();
        const isMore = value?.findIndex((item) => Number(item.value) === 1);
        const data = [...props.answerList];
        if (value?.length && validate()) {
            const response: AnswerItem = {
                question_items: value?.map((i) => ({
                    title: i.title,
                    id: i.payload,
                    payload: i.payload,
                    image: i.image,
                    value: isMore !== undefined && isMore > -1 ? '1' : undefined,
                })),
                question: props.question,
                id: new Date().getTime(),
                answer: props.answer?.id,
            };
            if (isMore !== undefined && isMore > -1) response.value = text;
            index !== undefined && index > -1 ? (data[index] = response) : data?.push(response);
        } else if (index !== undefined && index > -1) data.splice(index, 1);
        props.setAnswerList(data);
    }

    function onSubmit(e?: boolean) {
        const value = inputRef.current?.getValue();
        const isMore = value?.findIndex((item) => Number(item.value) === 1);
        if (
            (value?.length || props.answerList?.find((i) => i.id === question.id)?.question_items?.length) &&
            validate(e) &&
            props.answer
        ) {
            props.submit({
                answer: props.answer.id,
                question_items: value?.map((i) => ({
                    title: i.title,
                    id: i.payload,
                    payload: i.payload,
                    value: isMore !== undefined && isMore > -1 ? '1' : undefined,
                })),
                question: question,
                id: new Date().getTime(),
                value: isMore !== undefined && isMore > -1 ? text : '',
            });
        }
    }

    return (
        <SLPOptionsPhoto
            hint={extractTitle(hint, false, false)}
            ref={inputRef}
            counter={props.counter}
            label={extractTitle(title, false, false)}
            image={question.image_file}
            items={items}
            multiSelect={multi}
            color={props.form?.answer_color}
            labelColor={props.form?.question_color}
            onChange={setAnswer}
            onTextChange={(e) => setText(e)}
            required={question.is_required}
            imageSize={size}
            next={props.form.show_type === FormShowType.single ? props.next : undefined}
            dir={props.form.direction}
            defaultValue={
                index !== undefined && index > -1 && props.answerList[index].question_items
                    ? props.answerList[index].question_items?.map((i) => ({ title: i.title, payload: i.id }))
                    : undefined
            }
        />
    );
};
const mapStateToProps = (state: ReduxState) => ({
    direction: state.direction,
    lang: state.lang,
});

const connector = connect(mapStateToProps, null, null, { forwardRef: true });
export default connector(React.forwardRef(SLPPhotoOptionsQuestion));
